import { Controller } from "@hotwired/stimulus"
import { formatMoney } from "../modules/product_wizard.mjs"
import { previewPill, previewControl } from "../modules/product_options.mjs"

// Vista del cliente en vivo: pinta product-wizard:state; al conectar pide un refresh.
// Doc: _docs/features/products/partner_product_form.md
export default class extends Controller {
  static targets = ["photo", "image", "name", "chips", "price", "before", "badge", "description", "descriptionBody", "options", "ctaPrice"]

  connect() {
    const wizard = this.element.closest("[data-controller~='product-wizard']")
    const controller = wizard && this.application.getControllerForElementAndIdentifier(wizard, "product-wizard")
    controller?.refresh()
  }

  render(event) {
    const state = event.detail?.state
    if (!state) return
    this.nameTarget.textContent = state.name || "Nombre del producto"
    this.#renderPhoto(state.photoUrl)
    this.#renderChips(state.categories || [])
    this.#renderPrice(state)
    this.#renderDescription(state.description || "")
    this.#renderOptions(state.options)
  }

  // La descripción es HTML de Trix: se parsea en un <template> inerte y pasa por cleanDescription
  #renderDescription(html) {
    if (this.descriptionHtml === html) return
    this.descriptionHtml = html
    const template = document.createElement("template")
    template.innerHTML = html
    const body = cleanDescription(template.content, document.createDocumentFragment())
    const empty = body.textContent.trim() === ""
    empty ? this.descriptionBodyTarget.replaceChildren() : this.descriptionBodyTarget.replaceChildren(body)
    this.descriptionTarget.hidden = empty
  }

  // Por cada unidad y número: título y, por grupo, nombre, pill de la regla y alternativas con su control
  #renderOptions(options) {
    const units = options?.hasOptions ? options.units.filter(unit => unit.groups.length) : []
    const key = JSON.stringify(units) + options?.split
    if (this.optionsKey === key) return
    this.optionsKey = key
    if (!units.length) {
      this.optionsTarget.replaceChildren(element("p", "customer-preview__note", "Sin opciones: se agrega directo al carrito."))
      return
    }
    const blocks = []
    units.forEach(unit => {
      for (let number = 1; number <= Math.max(1, unit.quantity); number += 1) {
        const title = options.split ? (unit.quantity > 1 ? `${unit.label} #${number}` : unit.label).toUpperCase() : null
        blocks.push(this.#block(title, unit.groups))
      }
    })
    this.optionsTarget.replaceChildren(...blocks)
  }

  #block(title, groups) {
    const block = element("div", "customer-preview__block")
    if (title) block.appendChild(element("p", "customer-preview__block-title", title))
    groups.forEach(group => {
      const wrapper = element("div", "customer-preview__group")
      const head = element("div", "customer-preview__group-head")
      head.appendChild(element("span", "customer-preview__group-name", group.name))
      head.appendChild(element("span", `customer-preview__pill customer-preview__pill--${group.required ? "required" : "optional"}`, previewPill(group)))
      wrapper.appendChild(head)
      const list = element("ul", "customer-preview__alts")
      const control = previewControl(group)
      group.alternatives.forEach(alternative => {
        const item = element("li", "customer-preview__alt")
        const label = element("span", "customer-preview__alt-name", alternative.name)
        if (alternative.price > 0) label.appendChild(element("em", "customer-preview__alt-price", ` +${formatMoney(alternative.price)}`))
        item.appendChild(label)
        item.appendChild(this.#control(control))
        list.appendChild(item)
      })
      wrapper.appendChild(list)
      block.appendChild(wrapper)
    })
    return block
  }

  #control(kind) {
    if (kind !== "stepper") return element("span", `customer-preview__control customer-preview__control--${kind}`)
    const stepper = element("span", "customer-preview__control customer-preview__control--stepper")
    stepper.append(element("span", "customer-preview__mini", "−"), element("span", "customer-preview__mini-count", "0"), element("span", "customer-preview__mini", "+"))
    return stepper
  }

  // Con descuento vigente: precio final, el normal tachado y badge −N%, como la ficha
  #renderPrice(state) {
    const base = Number(state.price) || 0
    const discount = state.discount || {}
    const applied = discount.active && discount.final !== null && discount.final !== undefined
    const total = formatMoney(applied ? discount.final : base)
    this.priceTarget.textContent = total
    this.ctaPriceTarget.textContent = total
    this.beforeTarget.hidden = !applied
    this.badgeTarget.hidden = !applied
    if (applied) {
      this.beforeTarget.textContent = formatMoney(base)
      this.badgeTarget.textContent = `−${discount.percent}%`
    }
  }

  #renderPhoto(url) {
    if (url) {
      if (this.imageTarget.getAttribute("src") !== url) this.imageTarget.src = url
    } else {
      this.imageTarget.removeAttribute("src")
    }
    this.photoTarget.classList.toggle("has-image", Boolean(url))
  }

  #renderChips(labels) {
    const current = [...this.chipsTarget.children].map(chip => chip.textContent)
    if (current.length === labels.length && current.every((label, index) => label === labels[index])) return
    this.chipsTarget.replaceChildren(...labels.map(label => {
      const chip = document.createElement("span")
      chip.className = "customer-preview__chip"
      chip.textContent = label
      return chip
    }))
    this.chipsTarget.hidden = labels.length === 0
  }
}

function element(tag, className, text) {
  const node = document.createElement(tag)
  node.className = className
  if (text !== undefined) node.textContent = text
  return node
}

// Etiquetas que produce la toolbar del wizard (negrita, cursiva, lista) y los bloques de Trix
const DESCRIPTION_TAGS = ["DIV", "P", "BR", "STRONG", "B", "EM", "I", "UL", "OL", "LI"]

// Copia los hijos dejando solo texto y DESCRIPTION_TAGS sin atributos (script/style fuera, el resto se desenvuelve)
function cleanDescription(source, target) {
  source.childNodes.forEach(node => {
    if (node.nodeType === 3) {
      target.appendChild(document.createTextNode(node.textContent))
    } else if (node.nodeType === 1 && node.tagName !== "SCRIPT" && node.tagName !== "STYLE") {
      const keep = DESCRIPTION_TAGS.includes(node.tagName)
      const into = keep ? document.createElement(node.tagName) : target
      cleanDescription(node, into)
      if (keep) target.appendChild(into)
    }
  })
  return target
}
